import {BaseComponent} from '../base/component.base.js';
import template from './platform-costs.template.html';
import styles from './platform-costs.styles.css';
import {validatePlatformCostForm} from '../../core/billing/billing.core.js';
import {renderPlatformCosts} from './platform-costs.view.js';
import {
    fillPlatformCostForm,
    readPlatformCostForm,
    showPlatformCostFormErrors,
} from './platform-cost-form.view.js';

/**
 * The platform costs section of the billing overview: the cost table with an add/edit form. It owns
 * the rows, the open form and the range toggle, and only emits events; the driving adapter talks to
 * the API and hands the rows in through `costs`.
 *
 * Events (bubbling, composed): `platform-costs-range {showAll}`, `platform-cost-save {id | null,
 * request}` and `platform-cost-delete {id, label}` (after the user confirmed).
 */
export class PlatformCostsComponent extends BaseComponent {
    #showAll = false;
    /** @type {import('../../shell/types/billing.types.js').PlatformCostApiResponse[]} */
    #costs = [];
    /** The cost open in the form (`cost` is null for a new one); null while the form is closed. */
    #editing = null;
    #saving = false;

    connectedCallback() {
        this.loadTemplate(template, styles);
        this.$('#range-recent').addEventListener('click', () => this.#select(false));
        this.$('#range-all').addEventListener('click', () => this.#select(true));
        this.$('#cost-form').addEventListener('submit', (e) => this.#handleSubmit(e));
        this.shadowRoot.addEventListener('click', (e) => this.#handleClick(e));
        this.costs = [];
    }

    #select(showAll) {
        if (showAll === this.#showAll) return;
        this.showAll = showAll;
        this.emit('platform-costs-range', {showAll});
    }

    /** @param {boolean} value whether the list covers all time rather than the last 12 months */
    set showAll(value) {
        this.#showAll = value;
        for (const [id, active] of [['#range-recent', !value], ['#range-all', value]]) {
            const button = this.$(id);
            button.classList.toggle('active', active);
            button.setAttribute('aria-pressed', String(active));
        }
        renderPlatformCosts(this.$('#cost-list'), this.#costs, value);
    }

    get showAll() {
        return this.#showAll;
    }

    /**
     * Shows the rows. A cost open in the form that is gone from `costs` (deleted) closes the form.
     *
     * @param {import('../../shell/types/billing.types.js').PlatformCostApiResponse[]} costs newest first
     */
    set costs(costs) {
        this.#costs = costs;
        const editing = this.#editing?.cost;
        if (editing && !costs.some((cost) => cost.id === editing.id)) {
            this.closeForm();
        }
        renderPlatformCosts(this.$('#cost-list'), costs, this.#showAll);
    }

    /**
     * Whether a save is running: Save and Cancel are disabled, Save says so, and submits are ignored.
     *
     * @param {boolean} value
     */
    set saving(value) {
        this.#saving = value;
        this.$('#btn-submit-cost').disabled = value;
        this.$('#btn-cancel-cost').disabled = value;
        this.$('#btn-submit-cost span').textContent = value ? 'Saving...' : 'Save cost';
        this.$('#cost-form').setAttribute('aria-busy', String(value));
    }

    get saving() {
        return this.#saving;
    }

    /**
     * Shows why the backend rejected the cost, in the form where it gets fixed.
     *
     * @param {string | null} message null hides the error
     * @returns {boolean} true when the form is open and shows the message
     */
    showFormError(message) {
        const error = this.$('#cost-form-error');
        error.textContent = message || '';
        error.hidden = !message;
        return this.#editing !== null;
    }

    /** Closes the form and forgets what was typed in it. */
    closeForm() {
        this.#editing = null;
        this.$('#cost-form').hidden = true;
        this.showFormError(null);
    }

    /** @param {import('../../shell/types/billing.types.js').PlatformCostApiResponse | null} cost null opens the form for a new cost */
    #open(cost) {
        const form = this.$('#cost-form');
        this.#editing = {cost};
        fillPlatformCostForm(form, cost);
        this.showFormError(null);
        this.$('#cost-form-title').textContent = cost ? `Edit platform cost: ${cost.label}` : 'Add platform cost';
        form.hidden = false;
        this.$('#cost-supplier').focus();
    }

    #findCost(button) {
        return this.#costs.find((cost) => cost.id === Number(button.dataset.id));
    }

    async #handleClick(e) {
        if (e.target.closest('#btn-add-cost')) {
            this.#open(null);
        }
        const btnEdit = e.target.closest('.btn-cost-edit');
        if (btnEdit) {
            this.#open(this.#findCost(btnEdit));
        }
        if (e.target.closest('#btn-cancel-cost')) {
            this.closeForm();
        }
        const btnDelete = e.target.closest('.btn-cost-delete');
        if (btnDelete) {
            const cost = this.#findCost(btnDelete);
            const confirmed = await document.querySelector('admin-confirm-dialog').ask({
                title: `Delete platform cost "${cost.label}"?`,
                body: 'The cost is removed and no longer counts in the platform result or the income counter.',
                confirmLabel: 'Delete cost',
                cancelLabel: 'Cancel',
                danger: true,
            });
            if (confirmed) {
                this.emit('platform-cost-delete', {id: cost.id, label: cost.label});
            }
        }
    }

    #handleSubmit(e) {
        e.preventDefault();
        if (this.#saving) return;
        this.showFormError(null);
        const form = this.$('#cost-form');
        const {valid, errors, request} = validatePlatformCostForm(readPlatformCostForm(form));
        showPlatformCostFormErrors(form, errors);
        if (valid) {
            this.emit('platform-cost-save', {id: this.#editing.cost?.id ?? null, request});
        }
    }
}

customElements.define('platform-costs', PlatformCostsComponent);
